/* ==========================================================================
   Michael Vardani — hero slideshow

   Crossfades the hero between its images. A custom element rather than a
   document-level listener, because the theme editor re-renders the section on
   every settings change: connectedCallback picks the new markup up and
   disconnectedCallback clears the timer, so reloads never leave a stray
   interval running behind the visible one.

   The CSS does the fading. This only moves the data-active attribute.
   ========================================================================== */
(function () {
  'use strict';

  if (customElements.get('mv-hero-slideshow')) return;

  class MvHeroSlideshow extends HTMLElement {
    connectedCallback() {
      this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-mv-hero-slide]'));
      if (this.slides.length < 2) return;

      this.texts = Array.prototype.slice.call(this.querySelectorAll('[data-mv-hero-text]'));
      this.dots = Array.prototype.slice.call(this.querySelectorAll('[data-mv-hero-dot]'));

      var seconds = parseFloat(this.dataset.mvHeroInterval || '5');
      if (isNaN(seconds) || seconds < 2) seconds = 5;
      this.interval = seconds * 1000;
      this.pauseOnHover = this.dataset.mvHeroPause !== 'false';

      this.index = 0;
      for (var i = 0; i < this.slides.length; i++) {
        if (this.slides[i].hasAttribute('data-active')) {
          this.index = i;
          break;
        }
      }

      this.onDotClick = this.onDotClick.bind(this);
      this.onVisibility = this.onVisibility.bind(this);
      this.onEnter = this.onEnter.bind(this);
      this.onLeave = this.onLeave.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onFirstMove = this.onFirstMove.bind(this);

      this.addEventListener('click', this.onDotClick);
      document.addEventListener('visibilitychange', this.onVisibility);

      /* Pausing while a keyboard user is inside the hero is not optional — it
         is how they get time to read a link before it changes under them. */
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);

      /* Hover pausing is mouse-only. `mouseenter` rather than `pointerenter`
         because a touch tap fires pointerenter and never pointerleave, which
         would strand the slideshow on one image for the whole visit. */
      this.canHover = !!(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches);
      if (this.pauseOnHover && this.canHover) {
        this.addEventListener('mouseenter', this.onEnter);
        this.addEventListener('mouseleave', this.onLeave);
        /* The browser fires a boundary event when a full-bleed hero loads under
           a cursor that is already sitting still — which would pause the
           slideshow before the visitor has done anything. Hover only counts
           once the mouse has actually moved. */
        document.addEventListener('mousemove', this.onFirstMove, { once: true });
      }

      this.show(this.index);

      /* Someone who has asked for reduced motion gets the first image and the
         dots, and nothing that moves on its own. */
      this.reduce = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
      if (!this.reduce) this.start();
    }

    disconnectedCallback() {
      this.stop();
      this.removeEventListener('click', this.onDotClick);
      document.removeEventListener('visibilitychange', this.onVisibility);
      document.removeEventListener('mousemove', this.onFirstMove);
      this.removeEventListener('mouseenter', this.onEnter);
      this.removeEventListener('mouseleave', this.onLeave);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);
    }

    onFirstMove() {
      this.userMoved = true;
      /* If the cursor is already resting on the hero, honour it from here. */
      if (this.matches(':hover')) this.pause();
    }

    onEnter() {
      if (this.userMoved) this.pause();
    }

    onLeave() {
      this.resume();
    }

    onFocusIn() {
      this.pause();
    }

    onFocusOut() {
      /* focusout also fires when focus simply moves between two dots. */
      var self = this;
      window.setTimeout(function () {
        if (!self.contains(document.activeElement)) self.resume();
      }, 0);
    }

    start() {
      this.stop();
      var self = this;
      this.timer = window.setInterval(function () {
        self.show((self.index + 1) % self.slides.length);
      }, this.interval);
    }

    stop() {
      if (this.timer) window.clearInterval(this.timer);
      this.timer = null;
    }

    pause() {
      this.held = true;
      this.stop();
    }

    resume() {
      this.held = false;
      if (!this.reduce && !document.hidden) this.start();
    }

    onVisibility() {
      /* A background tab keeps firing intervals for nothing; worse, they all
         land at once when the tab comes back. */
      if (document.hidden) this.stop();
      else if (!this.held && !this.reduce) this.start();
    }

    onDotClick(event) {
      var dot = event.target.closest ? event.target.closest('[data-mv-hero-dot]') : null;
      if (!dot) return;
      var i = this.dots.indexOf(dot);
      if (i < 0 || i === this.index) return;
      this.show(i);
      /* Restart the clock so a deliberate choice gets its full turn. */
      if (!this.reduce && !this.held && !document.hidden) this.start();
    }

    show(i) {
      this.index = i;

      /* Each slide carries its own brightness and text position. Mirroring them
         onto the root is what lets one CSS rule set drive the text colour, the
         wash and the button polarity together — a pale slide and a dark one
         need opposite treatments, and the crossfade has to carry the text
         across with the picture. */
      var active = this.slides[i];
      if (active) {
        if (active.dataset.scheme) this.dataset.heroScheme = active.dataset.scheme;
        if (active.dataset.pos) this.dataset.heroPos = active.dataset.pos;
      }

      this.slides.forEach(function (slide, n) {
        if (n === i) slide.setAttribute('data-active', '');
        else slide.removeAttribute('data-active');
      });
      this.texts.forEach(function (text, n) {
        if (n === i) {
          text.setAttribute('data-active', '');
          text.removeAttribute('aria-hidden');
        } else {
          text.removeAttribute('data-active');
          text.setAttribute('aria-hidden', 'true');
        }
      });
      this.dots.forEach(function (dot, n) {
        if (n === i) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }
  }

  customElements.define('mv-hero-slideshow', MvHeroSlideshow);
})();
